import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { Temporal } from 'temporal-polyfill';
import { usePollJobOperations } from '~/api/job-operations';
import { useGetDemandAssociatedFilterValues, useGetSupplyAssociatedFilterValues } from '~/api/metrics';
import {
  demandAssociatedFilterValuesResponseSchema,
  supplyAssociatedFilterValuesResponseSchema,
} from '~/api/schemas/associated-filter-values.schemas';
import {
  type JobOperationPollResponse,
  jobOperationExecutionTypeEnum,
  jobOperationStatusEnum,
} from '~/api/schemas/job-operations.schemas';
import { formatOperationTypeLabel } from '~/components/dialogs/async-operations-dialog/operation-type-label';
import { useAsyncOperations, useWatchedOperationIds } from '~/global-state/async-operations';
import { useToast } from '~/global-state/toast';
import { useUserConfig } from '~/global-state/userConfig';
import { useFormatMessage } from '~/intl';
import { FEATURE_FLAGS, useFeatureFlag } from './useFeatureFlag';

const POLL_INTERVAL_MS = 15_000; // 15 seconds

export function useBackgroundQueries(isUserReady: boolean) {
  useBackgroundPrefetchQueries(isUserReady);
  usePollBackgroundQueries(isUserReady);
}

function useBackgroundPrefetchQueries(isUserReady: boolean) {
  const { isAuthenticated } = useAuth0();
  const dynamicFiltersDisabled = useFeatureFlag(FEATURE_FLAGS.dynamic_filters_disabled);

  const enabled = isUserReady && isAuthenticated && !dynamicFiltersDisabled;

  // Prefetch associated filter values for the dashboards.
  // Errors are silenced since this is not critical at app startup.
  useGetDemandAssociatedFilterValues({
    enabled,
    meta: { disableGlobalErrorHandler: true },
    responseSchema: demandAssociatedFilterValuesResponseSchema,
  });

  useGetSupplyAssociatedFilterValues({
    enabled,
    meta: { disableGlobalErrorHandler: true },
    responseSchema: supplyAssociatedFilterValuesResponseSchema,
  });
}

const jobOperationToToastAppearance = {
  [jobOperationStatusEnum.enum.QUEUED]: 'info',
  [jobOperationStatusEnum.enum.IN_PROGRESS]: 'info',
  [jobOperationStatusEnum.enum.COMPLETED]: 'success',
  [jobOperationStatusEnum.enum.FAILED]: 'error',
} as const;

function usePollBackgroundQueries(isUserReady: boolean) {
  const formatMessage = useFormatMessage();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const { isAuthenticated } = useAuth0();
  const {
    user: { isEndConsumer },
  } = useUserConfig();

  const { asyncOperations, handleSetCursor, handleSetHasUnreadEvents, handleSetEventIdsWithWarnings } =
    useAsyncOperations();
  const { isPanelOpen, panelLastClosedAt } = asyncOperations;
  const watchedOperationIds = useWatchedOperationIds();

  // Track the session start so we can tell whether a poll result has caught up to it.
  // We don't want to show toasts for events that occurred before the session started.
  const sessionStartRef = useRef(Temporal.Now.instant());

  const lastProcessedDataRef = useRef<JobOperationPollResponse | undefined>(undefined);

  const processPollData = useCallback(
    (data: JobOperationPollResponse) => {
      // If the cursor is stale, the poll has fallen behind the session
      // skip ahead to session start
      const isCatchingUp = Temporal.Instant.compare(data.next_after, sessionStartRef.current) < 0;
      handleSetCursor(isCatchingUp ? sessionStartRef.current : data.next_after);

      if (data.warning_event_ids.length > 0) {
        handleSetEventIdsWithWarnings(data.warning_event_ids);
      }

      if (!data.events.length) {
        return;
      }

      // Unread + toast only apply while the panel is closed.
      if (isPanelOpen) {
        return;
      }

      const hasNewUnread = data.events.some((event) => {
        if (!panelLastClosedAt) {
          return true;
        }

        const operationDate = event.completed_at ?? event.queued_at;
        return Temporal.Instant.compare(operationDate, panelLastClosedAt) > 0;
      });

      if (hasNewUnread) {
        handleSetHasUnreadEvents(true);
      }

      const sessionEvents = data.events.filter((event) => {
        // An immediate edit already confirmed itself on the page that made it, so it gets no toast.
        if (event.execution_type === jobOperationExecutionTypeEnum.enum.SYNC) {
          return false;
        }

        // A page waiting on this operation reports its outcome itself.
        if (watchedOperationIds.has(event.id)) {
          return false;
        }

        const operationDate = event.completed_at ?? event.queued_at;
        const isAfterSessionStart = Temporal.Instant.compare(operationDate, sessionStartRef.current) > 0;
        const isAfterPanelClosed = !panelLastClosedAt || Temporal.Instant.compare(operationDate, panelLastClosedAt) > 0;
        return isAfterSessionStart && isAfterPanelClosed;
      });

      if (sessionEvents.length) {
        const event = sessionEvents[0];

        const completedWithWarning = event.status === jobOperationStatusEnum.enum.COMPLETED && event.has_warnings;
        const appearance = completedWithWarning ? 'warning' : jobOperationToToastAppearance[event.status];
        const operationType = formatOperationTypeLabel(formatMessage, event.operation_type);

        const title = formatMessage(
          { id: `asyncOperations.notification.${appearance}.title` },
          { operationType: operationType },
        );

        addToast({
          appearance,
          title,
          id: 'job-operations-notification',
        });
      }
    },
    [
      handleSetCursor,
      handleSetEventIdsWithWarnings,
      handleSetHasUnreadEvents,
      addToast,
      formatMessage,
      isPanelOpen,
      panelLastClosedAt,
      watchedOperationIds,
    ],
  );

  const { data } = usePollJobOperations(asyncOperations.nextAfter, {
    enabled: isUserReady && isAuthenticated && !isEndConsumer,
    refetchInterval: POLL_INTERVAL_MS,
    limit: 1,
  });

  useEffect(() => {
    if (!data || lastProcessedDataRef.current === data) {
      return;
    }

    lastProcessedDataRef.current = data;
    processPollData(data);
  }, [data, processPollData]);

  // Invalidate queries when the panel is opened, so that the user sees the latest data.
  useEffect(() => {
    if (isPanelOpen && data?.events?.length) {
      queryClient.invalidateQueries({ queryKey: ['jobOperations', 'getJobOperations'] });
    }
  }, [data, isPanelOpen, queryClient]);
}
